import { ILoginData } from "@/domain/entities/login";
import { ILoginErrorData } from "@/domain/entities/login/loginError.types";
import { IResultData } from "@/domain/entities/shared/result";
import {
  ICompanyData,
  ICoordinateData,
  IFindIdData,
  IQrCodeData,
  IUserInfo,
} from "@/domain/entities/user";
import {
  ICertInfo,
  INoticeConfig,
  IShouldChangePasswordInfo,
  IUserRemainBookCount,
  IUserVerifyEmployeeNumber,
} from "@/domain/entities/user/user.types";
import TYPES from "@/ioc/identifiers";
import { container } from "@/ioc/inversify.config";
import {
  ITermsResult,
  IUserRepository,
} from "@/repositories/user/userRepository.types";
import { ACCESS_TOKEN, REFRESH_TOKEN } from "@/types";
import useRemoveTokens from "@/use/useRemoveTokens";
import useSetResultCodeIfNeeded from "@/use/useSetResultCodeIfNeeded";
import useSetTokens from "@/use/useSetTokens";
import { defineStore } from "pinia";
import { AUTO_LOGIN, MEMBER_ID, RankCode } from "./../types/index";
import { handlePasswordExpiredInfo } from "@/utils/handlePasswordExpiredInfo";
import dayjs from "dayjs";
import { IReportHistory } from "@/domain/entities/report";

interface IUserState {
  isMenuOpened: boolean;
  shouldShowNotice: boolean;
  selectedLineNum: string;
  isNotificationAllowed: boolean;
  termsResult: ITermsResult[];
  pushToken: string;
  userInfo: IUserInfo | undefined;
  companyList: ICompanyData[];
  qrCode: string;
  loginId: string;
  certInfo?: ICertInfo;
  isBookBlocked: boolean;
  bookBlockReleaseDisplay: string;
  isBoardBlocked: boolean;
  boardBlockReleaseDisplay: string;
  remainBookCount?: number;
}

const userRepository = () => {
  return container.get<IUserRepository>(TYPES.IUserRepository);
};

export const useUserStore = defineStore("user", {
  state: (): IUserState => {
    return {
      isMenuOpened: false,
      shouldShowNotice: false,
      selectedLineNum: "",
      isNotificationAllowed: false,
      termsResult: [],
      pushToken: "",
      userInfo: undefined,
      companyList: [],
      qrCode: "",
      loginId: "",
      certInfo: undefined,
      isBookBlocked: false,
      bookBlockReleaseDisplay: "",
      isBoardBlocked: false,
      boardBlockReleaseDisplay: "",
      remainBookCount: undefined,
    };
  },
  actions: {
    updateMenuStatus(isOpened: boolean) {
      this.isMenuOpened = isOpened;
    },
    updateShouldShowNoticeStatus(shouldShowNotice: boolean) {
      this.shouldShowNotice = shouldShowNotice;
    },
    updateSelectedLineNum(lineNum: string) {
      this.selectedLineNum = lineNum;
    },
    updateNotificationAllowStatus(isAllowed: boolean) {
      this.isNotificationAllowed = isAllowed;
    },
    updateUserId(id: string) {
      this.loginId = id;
    },
    updatePhoneNumber(phoneNumber: string) {
      if (!this.userInfo) return;

      this.userInfo.phoneNumber = phoneNumber;
    },
    setTermsResult(termsList: ITermsResult[]) {
      this.termsResult = termsList;
    },
    setPushToken(token: string) {
      this.pushToken = token;
    },
    /** 예약 불가 상태를 저장한다 */
    setBookBlocked(blocked: boolean) {
      this.isBookBlocked = blocked;
    },
    /** 예약 불가 해제 표시문구를 저장한다 */
    setBookBlockReleaseDisplay(display: string) {
      this.bookBlockReleaseDisplay = display;
    },
    /** 탑승 불가 상태를 저장한다 */
    setBoardBlocked(blocked: boolean) {
      this.isBoardBlocked = blocked;
    },
    /** 탑승 불가 해제 표시문구를 저장한다 */
    setBoardBlockReleaseDisplay(display: string) {
      this.boardBlockReleaseDisplay = display;
    },

    async checkDuplicateId(userId: string): Promise<IResultData<boolean>> {
      const checkDuplicateIdResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await userRepository()
        .checkDuplicateId(userId)
        .then((result) => {
          result
            .map((resData) => {
              checkDuplicateIdResult.isSuccess = resData.isSuccess;
              checkDuplicateIdResult.data = resData.isSuccess;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(checkDuplicateIdResult, error);
              checkDuplicateIdResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          checkDuplicateIdResult.errorMessage = error.message;
        });

      return checkDuplicateIdResult;
    },

    async sendEmailVerificationCode(
      email: string
    ): Promise<IResultData<boolean>> {
      const sendEmailVerificationResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await userRepository()
        .sendEmailVerificationCode(email)
        .then((result) => {
          result
            .map((resData) => {
              sendEmailVerificationResult.isSuccess = resData.isSuccess;
              sendEmailVerificationResult.data = resData.isSuccess;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(sendEmailVerificationResult, error);
              sendEmailVerificationResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          sendEmailVerificationResult.errorMessage = error.message;
        });

      return sendEmailVerificationResult;
    },

    async checkEmailVerificationCode(
      email: string,
      certiCode: string
    ): Promise<IResultData<boolean>> {
      const checkEmailVerificationCodeResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await userRepository()
        .checkEmailVerificationCode(email, certiCode)
        .then((result) => {
          result
            .map((resData) => {
              checkEmailVerificationCodeResult.isSuccess = resData.isSuccess;
              checkEmailVerificationCodeResult.data = resData.isSuccess;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(checkEmailVerificationCodeResult, error);
              checkEmailVerificationCodeResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          checkEmailVerificationCodeResult.errorMessage = error.message;
        });

      return checkEmailVerificationCodeResult;
    },

    async sendPhoneVerificationCode(
      phoneNumber: string
    ): Promise<IResultData<boolean>> {
      const sendVerificationResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await userRepository()
        .sendPhoneVerificationCode(phoneNumber)
        .then((result) => {
          result
            .map((resData) => {
              sendVerificationResult.isSuccess = resData.isSuccess;
              sendVerificationResult.data = resData.isSuccess;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(sendVerificationResult, error);
              sendVerificationResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          sendVerificationResult.errorMessage = error.message;
        });

      return sendVerificationResult;
    },

    async checkPhoneVerificationCode(
      phoneNumber: string,
      certNo: string
    ): Promise<IResultData<boolean>> {
      const checkVerificationResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await userRepository()
        .checkPhoneVerificationCode(phoneNumber, certNo)
        .then((result) => {
          result
            .map((resData) => {
              checkVerificationResult.isSuccess = resData.isSuccess;
              checkVerificationResult.data = resData.isSuccess;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(checkVerificationResult, error);
              checkVerificationResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          checkVerificationResult.errorMessage = error.message;
        });

      return checkVerificationResult;
    },

    async verifyEmployeeNumber(
      eziokCertAuthValueEnc: string,
      employeeNo: string
    ): Promise<IResultData<IUserVerifyEmployeeNumber>> {
      const verifyResult: IResultData<IUserVerifyEmployeeNumber> = {
        isSuccess: false,
      };

      await userRepository()
        .verifyEmployeeNumber(eziokCertAuthValueEnc, employeeNo)
        .then((result) => {
          result
            .map((resData) => {
              verifyResult.isSuccess = true;
              verifyResult.data = resData;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(verifyResult, error);
              verifyResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          verifyResult.errorMessage = error.message;
        });

      return verifyResult;
    },

    async signUp(
      rankCode: string,
      signUpValue: Record<string, any>
    ): Promise<IResultData<boolean>> {
      const signUpResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await userRepository()
        .signUp(rankCode, signUpValue, this.termsResult)
        .then((result) => {
          result
            .map((resData) => {
              signUpResult.isSuccess = resData.isSuccess;
              signUpResult.data = resData.isSuccess;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(signUpResult, error);
              signUpResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          signUpResult.errorMessage = error.message;
        });

      return signUpResult;
    },

    async login(
      userId: string,
      password: string,
      isAutoLogin: boolean
    ): Promise<IResultData<ILoginData | ILoginErrorData>> {
      this.updateUserId(userId);

      const loginResult: IResultData<ILoginData | ILoginErrorData> = {
        isSuccess: false,
      };

      await userRepository()
        .login(userId, password, this.pushToken)
        .then((result) => {
          result
            .map((result) => {
              loginResult.isSuccess = true;
              loginResult.data = result;
              window.localStorage.setItem(MEMBER_ID, result.memberUid);
              window.localStorage.setItem(AUTO_LOGIN, String(isAutoLogin));
              useSetTokens(result.accessToken, result.refreshToken);
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(loginResult, error);
              loginResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          loginResult.errorMessage = error.message;
        });

      return loginResult;
    },

    async loginWithKakao(
      isAutoLogin: boolean,
      redirectUri: string,
      authCode: string
    ): Promise<IResultData<ILoginData>> {
      const kakaoLoginResult: IResultData<ILoginData> = {
        isSuccess: false,
      };

      await userRepository()
        .loginWithKakao(redirectUri, authCode)
        .then((result) => {
          result
            .map((result) => {
              kakaoLoginResult.isSuccess =
                !!result.accessToken && !!result.refreshToken;
              kakaoLoginResult.data = result;

              window.localStorage.setItem(AUTO_LOGIN, String(isAutoLogin));
              if (isAutoLogin) {
                window.localStorage.setItem(ACCESS_TOKEN, result.accessToken);
                window.localStorage.setItem(REFRESH_TOKEN, result.refreshToken);
              } else {
                window.sessionStorage.setItem(ACCESS_TOKEN, result.accessToken);
                window.sessionStorage.setItem(
                  REFRESH_TOKEN,
                  result.refreshToken
                );
              }
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(kakaoLoginResult, error);
              kakaoLoginResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          kakaoLoginResult.errorMessage = error.message;
        });

      return kakaoLoginResult;
    },

    async loginWithNaver(
      isAutoLogin: boolean,
      state: string,
      authCode: string
    ): Promise<IResultData<ILoginData>> {
      const naverLoginResult: IResultData<ILoginData> = {
        isSuccess: false,
      };

      await userRepository()
        .loginWithNaver(state, authCode)
        .then((result) => {
          result
            .map((result) => {
              naverLoginResult.isSuccess =
                !!result.accessToken && !!result.refreshToken;
              naverLoginResult.data = result;

              window.localStorage.setItem(AUTO_LOGIN, String(isAutoLogin));
              if (isAutoLogin) {
                window.localStorage.setItem(ACCESS_TOKEN, result.accessToken);
                window.localStorage.setItem(REFRESH_TOKEN, result.refreshToken);
              } else {
                window.sessionStorage.setItem(ACCESS_TOKEN, result.accessToken);
                window.sessionStorage.setItem(
                  REFRESH_TOKEN,
                  result.refreshToken
                );
              }
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(naverLoginResult, error);
              naverLoginResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          naverLoginResult.errorMessage = error.message;
        });

      return naverLoginResult;
    },

    async getNoticeConfig(): Promise<IResultData<INoticeConfig>> {
      const getNoticeResult: IResultData<INoticeConfig> = {
        isSuccess: false,
      };

      await userRepository()
        .getNoticeConfig()
        .then((result) => {
          result
            .map((config) => {
              getNoticeResult.isSuccess = true;
              getNoticeResult.data = config;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(getNoticeResult, error);
              getNoticeResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          getNoticeResult.errorMessage = error.message;
        });

      return getNoticeResult;
    },

    async setNoticeConfig(
      receiveNotice: boolean
    ): Promise<IResultData<INoticeConfig>> {
      const setNoticeConfigResult: IResultData<INoticeConfig> = {
        isSuccess: false,
      };

      await userRepository()
        .setNoticeConfig(receiveNotice)
        .then((result) => {
          result
            .map((config) => {
              setNoticeConfigResult.isSuccess = true;
              setNoticeConfigResult.data = config;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(setNoticeConfigResult, error);
              setNoticeConfigResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          setNoticeConfigResult.errorMessage = error.message;
        });

      return setNoticeConfigResult;
    },

    async findId(
      userName: string,
      phoneNumber: string
    ): Promise<IResultData<IFindIdData>> {
      const findIdResult: IResultData<IFindIdData> = {
        isSuccess: false,
      };

      await userRepository()
        .findId(userName, phoneNumber)
        .then((result) => {
          result
            .map((result) => {
              findIdResult.isSuccess = true;
              findIdResult.data = result;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(findIdResult, error);
              findIdResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          findIdResult.errorMessage = error.message;
        });

      return findIdResult;
    },

    async findPassword(
      phoneNumber: string,
      userId?: string
    ): Promise<IResultData<boolean>> {
      const findPasswordResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await userRepository()
        .findPassword(phoneNumber, userId)
        .then((result) => {
          result
            .map((resData) => {
              findPasswordResult.isSuccess = resData.isSuccess;
              findPasswordResult.data = resData.isSuccess;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(findPasswordResult, error);
              findPasswordResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          findPasswordResult.errorMessage = error.message;
        });

      return findPasswordResult;
    },

    async getUserInfo(): Promise<IResultData<IUserInfo>> {
      const userInfoResult: IResultData<IUserInfo> = {
        isSuccess: false,
      };

      await userRepository()
        .getUserInfo()
        .then((result) => {
          result
            .map((userInfo) => {
              window.localStorage.setItem(MEMBER_ID, userInfo.uid);
              userInfoResult.isSuccess = true;
              userInfoResult.data = userInfo;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(userInfoResult, error);
              userInfoResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          userInfoResult.errorMessage = error.message;
        });

      this.userInfo = userInfoResult.data;
      return userInfoResult;
    },

    /**
     * 비밀번호 변경
     * @param currentPassword
     * @param newPassword
     * @returns
     */
    async changePassword(
      passwordValues: Record<string, any>
    ): Promise<IResultData<boolean>> {
      const changePasswordResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await userRepository()
        .changePassword(passwordValues)
        .then((result) => {
          result
            .map((result) => {
              changePasswordResult.isSuccess = result.isSuccess;
              changePasswordResult.data = result.isSuccess;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(changePasswordResult, error);
              changePasswordResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          changePasswordResult.errorMessage = error.message;
        });

      return changePasswordResult;
    },

    /**
     * 비밀번호 유효기간 만료 후 갱신
     * @param userId
     * @param currentPassword
     * @param newPassword
     * @returns
     */
    async updatePassword(
      userId: string,
      currentPassword: string,
      newPassword: string
    ): Promise<IResultData<ILoginData>> {
      this.updateUserId(userId);

      const changePasswordResult: IResultData<ILoginData> = {
        isSuccess: false,
      };

      await userRepository()
        .updatePassword(userId, currentPassword, newPassword)
        .then((result) => {
          result
            .map((result) => {
              changePasswordResult.isSuccess = true;
              changePasswordResult.data = result;
              useSetTokens(result.accessToken, result.refreshToken);
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(changePasswordResult, error);
              changePasswordResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          changePasswordResult.errorMessage = error.message;
        });

      return changePasswordResult;
    },

    async changePhoneNumber(
      phoneNumber: string,
      verificationCode: string
    ): Promise<IResultData<boolean>> {
      const changePhoneResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await userRepository()
        .changePhoneNumber(phoneNumber, verificationCode)
        .then((result) => {
          result
            .map((result) => {
              changePhoneResult.isSuccess = result.isSuccess;
              changePhoneResult.data = result.isSuccess;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(changePhoneResult, error);
              changePhoneResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          changePhoneResult.errorMessage = error.message;
        });

      return changePhoneResult;
    },

    async checkPassword(password: string): Promise<IResultData<boolean>> {
      const authResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await userRepository()
        .checkPassword(password)
        .then((result) => {
          result
            .map((result) => {
              authResult.isSuccess = result.isSuccess;
              authResult.data = result.isSuccess;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(authResult, error);
              authResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          authResult.errorMessage = error.message;
        });

      return authResult;
    },

    async checkIfPasswordNeedsChange(): Promise<
      IResultData<IShouldChangePasswordInfo>
    > {
      const checkResult: IResultData<IShouldChangePasswordInfo> = {
        isSuccess: false,
      };

      const { updateCheckPasswordCalledDate, updateValidEndDate } =
        handlePasswordExpiredInfo();

      updateCheckPasswordCalledDate(dayjs().format("YYYY-MM-DD HH:mm:ss"));

      await userRepository()
        .checkIfPasswordNeedsChange()
        .then((result) => {
          result
            .map((result) => {
              checkResult.isSuccess = true;
              checkResult.data = result;
              updateValidEndDate(
                dayjs(result.passwordValidEndDtm).format("YYYY-MM-DD HH:mm:ss")
              );
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(checkResult, error);
              checkResult.errorMessage = error.message;
            });
        });

      return checkResult;
    },

    async changeMyInfo(
      myInfoValue: Record<string, any>
    ): Promise<IResultData<boolean>> {
      const editMyInfoResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await userRepository()
        .changeMyInfo(myInfoValue)
        .then((result) => {
          result
            .map((result) => {
              editMyInfoResult.isSuccess = result.isSuccess;
              editMyInfoResult.data = result.isSuccess;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(editMyInfoResult, error);
              editMyInfoResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          editMyInfoResult.errorMessage = error.message;
        });

      return editMyInfoResult;
    },

    async updateToken(
      memberUid: string,
      refreshToken: string
    ): Promise<IResultData<ILoginData>> {
      const updateTokenResult: IResultData<ILoginData> = {
        isSuccess: false,
      };

      await userRepository()
        .updateToken(memberUid, refreshToken)
        .then((result) => {
          result
            .map((result) => {
              updateTokenResult.isSuccess = true;
              updateTokenResult.data = result;
              useSetTokens(result.accessToken, result.refreshToken);
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(updateTokenResult, error);
              updateTokenResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          updateTokenResult.errorMessage = error.message;
        });

      return updateTokenResult;
    },

    logout() {
      useRemoveTokens();
      this.userInfo = undefined; // userinfo 삭제
    },

    async updatePushToken(fcmToken: string): Promise<IResultData<boolean>> {
      const updatePushTokenResult: IResultData<boolean> = {
        isSuccess: false,
        data: false,
      };

      await userRepository()
        .updatePushToken(fcmToken)
        .then((result) => {
          result
            .map((result) => {
              updatePushTokenResult.isSuccess = result.isSuccess;
              updatePushTokenResult.data = result.isSuccess;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(updatePushTokenResult, error);
              updatePushTokenResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          updatePushTokenResult.errorMessage = error.message;
        });

      return updatePushTokenResult;
    },

    async getCompanyList(
      rankCode: RankCode
    ): Promise<IResultData<ICompanyData[]>> {
      const companyListResult: IResultData<ICompanyData[]> = {
        isSuccess: false,
        data: [],
      };

      await userRepository()
        .getCompanyList(rankCode)
        .then((result) => {
          result
            .map((result) => {
              companyListResult.isSuccess = true;
              companyListResult.data = result;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(companyListResult, error);
              companyListResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          companyListResult.errorMessage = error.message;
        });

      this.companyList = companyListResult.data ?? [];
      return companyListResult;
    },

    async getAddressCoordinate(
      roadAddress: string
    ): Promise<IResultData<ICoordinateData>> {
      const addressCoordinateResult: IResultData<ICoordinateData> = {
        isSuccess: false,
      };

      await userRepository()
        .getAddressCoordinate(roadAddress)
        .then((result) => {
          result
            .map((result) => {
              addressCoordinateResult.isSuccess = true;
              addressCoordinateResult.data = result;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(addressCoordinateResult, error);
              addressCoordinateResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          addressCoordinateResult.errorMessage = error.message;
        });

      return addressCoordinateResult;
    },

    async getQrCode(): Promise<IResultData<IQrCodeData>> {
      const qrCodeResult: IResultData<IQrCodeData> = {
        isSuccess: false,
      };

      await userRepository()
        .getQrCode()
        .then((result) => {
          result
            .map((result) => {
              qrCodeResult.isSuccess = true;
              qrCodeResult.data = result;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(qrCodeResult, error);
              qrCodeResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          qrCodeResult.errorMessage = error.message;
        });

      this.qrCode = qrCodeResult.data?.qrCode ?? "";
      return qrCodeResult;
    },

    /**
     * 가입 승인 담당자 구성원 승인/반려 API
     */
    async approveUserSignUp(
      certYn: boolean,
      memberUid: string
    ): Promise<IResultData<ICertInfo>> {
      const certResult: IResultData<ICertInfo> = {
        isSuccess: false,
      };

      await userRepository()
        .approveUserSignUp(certYn, memberUid)
        .then((result) => {
          result
            .map((result) => {
              certResult.isSuccess = true;
              certResult.data = result;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(certResult, error);
              certResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          certResult.errorMessage = error.message;
        });

      this.certInfo = certResult.data ?? undefined;
      return certResult;
    },

    /**
     * 계정 잠금 해제
     */

    async unlockAccount(
      loginId: string,
      eziokCertAuthValueEnc: string
    ): Promise<IResultData<boolean>> {
      const unlockResult: IResultData<boolean> = {
        isSuccess: false,
      };

      await userRepository()
        .unlockAccount(loginId, eziokCertAuthValueEnc)
        .then((result) => {
          result
            .map((result) => {
              unlockResult.isSuccess = true;
              unlockResult.data = true;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(unlockResult, error);
              unlockResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          unlockResult.errorMessage = error.message;
        });

      return unlockResult;
    },

    /** 비밀번호 만료 기한 연장 */
    async extendPasswordExpiration(): Promise<IResultData<boolean>> {
      const extendResult: IResultData<boolean> = {
        isSuccess: false,
      };

      const memberUid = window.localStorage.getItem(MEMBER_ID);

      await userRepository()
        .extendPasswordExpiration(memberUid ?? "")
        .then((result) => {
          result
            .map((resData) => {
              extendResult.isSuccess = true;
              extendResult.data = true;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(extendResult, error);
              extendResult.errorMessage = error.message;
            });
        })
        .catch((error) => {
          extendResult.errorMessage = error.message;
        });

      return extendResult;
    },

    async getRemainBookCount(): Promise<IResultData<IUserRemainBookCount>> {
      const bookCountResult: IResultData<IUserRemainBookCount> = {
        isSuccess: false,
      };

      await userRepository()
        .getRemainBookCount()
        .then((result) => {
          result
            .map((resData) => {
              bookCountResult.isSuccess = true;
              bookCountResult.data = resData;
              this.remainBookCount = resData.remainBookCount;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(bookCountResult, error);
              bookCountResult.errorMessage = error.message;
              this.remainBookCount = undefined;
            });
        })
        .catch((error) => {
          bookCountResult.errorMessage = error.message;
        });

      return bookCountResult;
    },

    async getReportHistory(
      fromDate: string,
      toDate: string
    ): Promise<IResultData<IReportHistory[]>> {
      const reportHistoryResult: IResultData<IReportHistory[]> = {
        isSuccess: false,
      };

      await userRepository()
        .getReportHistory(fromDate, toDate)
        .then((result) => {
          result
            .map((resData) => {
              reportHistoryResult.isSuccess = true;
              reportHistoryResult.data = resData;
            })
            .mapErr((error) => {
              useSetResultCodeIfNeeded(reportHistoryResult, error);
              reportHistoryResult.errorMessage = error.message;
              this.remainBookCount = undefined;
            });
        })
        .catch((error) => {
          reportHistoryResult.errorMessage = error.message;
        });

      return reportHistoryResult;
    },
  },
});
